import { Component, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';

import { first } from 'rxjs/operators';

import { DialogService } from "../../../common/services/dialog.service";
import { EntityBuilderService } from "../../services/entity-builder.service"; 
import { SidePanelService } from "../../../common/services/side-panel.service";

import { deepClone } from "../../models/editor.model";
import { EntityMap,LunaEntity, PropertyMap, RelatedEntityMap } from "../../models/entity.model";
import { FlowTemplate } from "../../../common/models/flow.dto";
import { SidePanel } from "../../../common/models/side-panel.model";

@Component({
    templateUrl: "./workflow-entity.component.html",
    selector: "workflow-entity",
    styleUrls: ["./workflow-entity.component.scss"],
    providers: [DialogService],
    standalone: false
})

export class WorkflowEntityComponent implements OnInit, OnChanges {

  @Input() public template: FlowTemplate;
  @Input() public copiedContext: string = "";

  public allowArrayMapping: boolean = false;
  public availableEntities = new Array<LunaEntity>();
  public defaultEntity = { id: "-1", entityName : "...", collectionName: "", schemaObject: null } as LunaEntity;
  public entityToAdd: string;
  public defaultPropertyOption = { propertyName: "...", propertyType: "", allowMultipleMaps: false, contextPath: "", isPrimitive : true } as PropertyMap;
  public relationalPropertyOptions: PropertyMap[] = [];
  public _showEntityEditor: boolean = false;
  public _selectedEntity: LunaEntity | null = null;
    public selectedMap: EntityMap | null = null;
  public selectedRelatedMap: RelatedEntityMap | null;
  public editorSidePanel: SidePanel = new SidePanel({
    tabs: [{ title: "Add Entity" }],
    sections: [{ componentName: "WorkflowEntityAtlas", componentData: { entityMap: this.selectedMap, entity: this.selectedEntity, contextValue: this.copiedContext }, method: (event) => this.entityMapCreated(event)}],
    visible: false
  });
  public importSidePanel: SidePanel = new SidePanel({
    tabs: [{ title: "Import Entity Map" }],
    sections: [{ componentName: "WorkflowEntityImport", method: (event) => this.mapFileImported(event) }],
    visible: false
  });
  public map: EntityMap;
  public index: number;

  set showEntityEditor(value: boolean) {
    this._showEntityEditor = value;
    //Needed: Set menu focus to first input (removed for reusable solution)
  }
  get showEntityEditor() {
    return this._showEntityEditor;
  }
  set selectedEntity(value: LunaEntity | null) {
    this._selectedEntity = value;
    this.showEntityEditor = this._selectedEntity !== null;
  }
  get selectedEntity(): LunaEntity | null {
    return this._selectedEntity;
  }
  get addEntityButtonDisabled(): boolean {
    if (this.entityToAdd == undefined) {
    return true;
    } else {
    return this.entityToAdd === "-1";
    }
  }

  constructor(
    public entityBuilderService: EntityBuilderService,
    public sidePanelService: SidePanelService,
    public readonly dialogService: DialogService 
  ) { }

  ngOnInit(): void {
    this.availableEntities.push(this.defaultEntity);
    this.entityToAdd = "-1";
    this.loadAvailableEntities();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["copiedContext"]) {
      this.sidePanelService.updateSectionData("WorkflowEntityAtlas", { entityMap: this.selectedMap, entity: this.selectedEntity, contextValue: this.copiedContext });
    }
  }

  public addRelatedButtonDisabled(map: EntityMap, index: number): boolean {
    const related = map.relatedEntityMaps[index];
    return related.entity === this.defaultEntity || related.relationalProperty === "";
  }

  public addEntity(): void {
    if (this.entityToAdd === undefined || this.entityToAdd === null || this.entityToAdd === "-1") {
      this.selectedEntity = null;
      return;
    }
    const entity = this.availableEntities.find(e => e.id === this.entityToAdd);
    if (entity === undefined || entity === null || entity === this.defaultEntity) {
      this.selectedEntity = null;
      return;
    }
    this.selectedEntity = entity;
      this.selectedMap = this.entityBuilderService.generateEntityMap(this.selectedEntity);

    //update values in side panel with latest values
    this.editorSidePanel.sections[0].componentData = { template: this.template, copiedContext: this.copiedContext, entityMap: this.selectedMap };
    this.sidePanelService.newSidePanel(this.editorSidePanel);
    this.sidePanelService.toggleSidePanel(true);
  }

  public addRelatedEntity(relatedEntity: RelatedEntityMap): void {
    this.editorSidePanel.sections[0].componentData = {
      template: this.template,
      copiedContext: this.copiedContext,
      entityMap: relatedEntity
    };
    this.sidePanelService.newSidePanel(this.editorSidePanel);
    this.sidePanelService.toggleSidePanel(true);
  }

  public addNewRelated(map: EntityMap): void {
    if (map) {
      if (!map.relatedEntityMaps) { map.relatedEntityMaps = [] };
      const related = this.createNewRelatedMap(map.id);
      map.relatedEntityMaps.push(related);

    }
  }

  public beginImport(): void {
    this.sidePanelService.newSidePanel(this.importSidePanel);
    this.sidePanelService.toggleSidePanel(true);
  }

  public createNewMap(id: string, entityName: string): EntityMap {
    return { id: id, mapName: entityName, propertyMaps: [], relatedEntityMaps: [] } as EntityMap;
  }

  public createNewRelatedMap(parentMapId: string): RelatedEntityMap {
    return { id: "-1", entity: this.defaultEntity, mapName: "", parentMapId: parentMapId, propertyMaps: [], relatedEntityMaps: [], relationalProperty: "", isDirty: true } as RelatedEntityMap;
  }

  public deleteMap(mapName: string, index: number): void {
    this.index = index;
    this.dialogService.openDialog("confirmCancel", `Please confirm deletion for the map ${mapName}.  Any Side Effects referencing this map will need to be manually updated.`, "deleteMap");
  }

  public deleteRelated(map: EntityMap, related: RelatedEntityMap, index: number): void {
    this.map = map;
    this.index = index;
    this.dialogService.openDialog("confirmCancel", `Please confirm deletion for the map ${related.mapName} which is related to ${map.mapName} via the ${related.relationalProperty} property.  Any Side Effects referencing this related map will need to be manually updated.`, "deleteRelated");
  }

  public editMap(id: string): void {
      const map = this.template.entityMaps.find(m => m.id === id);
    this.selectedMap = deepClone(map);
    if (this.selectedMap !== undefined && this.selectedMap !== null) {
      this.selectedEntity = this.selectedMap.entity;

      this.sidePanelService.newSidePanel(this.editorSidePanel);
      this.sidePanelService.updateSectionData("WorkflowEntityAtlas", { template: this.template, copiedContext: this.copiedContext, entityMap: this.selectedMap });
      this.sidePanelService.toggleSidePanel(true);
    } else {
      this.selectedMap = null;
    }
  }

  public editRelated(map: RelatedEntityMap): void {
    const toEdit = deepClone(map);
    this.sidePanelService.newSidePanel(this.editorSidePanel);
    this.sidePanelService.updateSectionData("WorkflowEntityAtlas", { template: this.template, copiedContext: this.copiedContext, entityMap: toEdit });
    this.sidePanelService.toggleSidePanel(true);
  }

  public entityMapCreated(map: EntityMap | RelatedEntityMap | null): void {
    if (map !== null) {
      if (this.template.entityMaps === undefined || this.template.entityMaps === null) {
        this.template.entityMaps = new Array<EntityMap>();
      }
      const bubble = new Array<EntityMap>();
      if (!this.entityBuilderService.isRelationalMap(map)) {
        bubble.push(map);
      }
      for (let i = 0; i < this.template.entityMaps.length; i++) {
        if (this.entityBuilderService.isRelationalMap(map)) {
          if (this.template.entityMaps[i].id === map.parentMapId) {
            const parentMap = this.template.entityMaps[i];
            const entityBubble = new Array<RelatedEntityMap>();
            entityBubble.push(map);
            for (let r = 0; r < parentMap.relatedEntityMaps.length; r++) {
              if (parentMap.relatedEntityMaps[r].id !== map.id) {
                entityBubble.push(parentMap.relatedEntityMaps[r]);
              }
            }
            parentMap.relatedEntityMaps = entityBubble;
          }
        }
        if (this.template.entityMaps[i].id !== map.id) {
          bubble.push(this.template.entityMaps[i]);
        }
      }
      this.template.entityMaps = bubble;
    }
    this.selectedEntity = null;
    this.entityToAdd = "-1";
    this.addEntity();
  }

  public loadAvailableEntities(): void {
    this.entityBuilderService.getActive().pipe(first()).subscribe(response => {
      response.forEach(e => {
        this.availableEntities.push(e);
      });
    },
      error => {
        console.error(error);
      });
  }

  public mapFileImported(maps: EntityMap[]): void {
    if (maps) {
      this.template.entityMaps = maps;
  }
  }

  public mapHasRelatedEntities(map: EntityMap): boolean {
    return (map.relatedEntityMaps && map.relatedEntityMaps.length > 0);
  }

  public relatedEntityChanged(entityName: string, map: EntityMap, index: number): void {
    if (map) {
      const entity = this.availableEntities.find(e => e.id === entityName);
      if (entity && entity !== this.defaultEntity) {
        const newRelated = this.entityBuilderService.generateEntityMap(entity) as RelatedEntityMap;
        newRelated.parentMapId = map.id;
        newRelated.relationalProperty = "";
        newRelated.isDirty = true;
        map.relatedEntityMaps.splice(index, 1);
        map.relatedEntityMaps.push(newRelated);
        this.setRelationalOptions(newRelated);
      }
    }
  }

  public relationalPropertyChanged(property: string, map: EntityMap, index: number): void {
    const related = map.relatedEntityMaps[index];
    related.relationalProperty = property;
  }

  public setRelationalOptions(map: RelatedEntityMap): void {
    this.relationalPropertyOptions = [];
    this.relationalPropertyOptions.push(this.defaultPropertyOption);
    const options = deepClone(map.propertyMaps);
    for (let p = 0; p < options.length; p++) {
      this.relationalPropertyOptions.push(options[p]);
    }
  }

  public onDialogConfirm(): void {
    this.dialogService.closeDialog();
    if (this.dialogService.dialogId === 'deleteMap') {
      this.template.entityMaps.splice(this.index, 1);
    } else if (this.dialogService.dialogId === 'deleteRelated') {
      this.map.relatedEntityMaps.splice(this.index, 1);
    }
  }

  public onDialogClose(): void {
    this.dialogService.closeDialog();
  }

}
